import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface HreflangAlt {
  hreflang: string; // e.g. 'en', 'fr', 'es', 'x-default'
  href: string;
}

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  canonical?: string;
  ogImage?: string;
  hreflangs?: HreflangAlt[];
}

const defaultSEO = {
  title: "🆓 FREE Digital Assessment | QuantumBox AI",
  description: "Get your FREE Digital Transformation Assessment! Complete analysis + strategic recommendations. 40% faster than competitors, 60% cost reduction.",
  keywords: "free digital transformation assessment, free AI assessment report, digital readiness evaluation, free business assessment, digital transformation consulting, AI business automation",
  ogTitle: "🆓 FREE Digital Assessment | QuantumBox AI",
  ogDescription: "Get your FREE Digital Transformation Assessment! Complete analysis + strategic recommendations. Download your free PDF report instantly."
};

export const useSEO = (seoProps?: SEOProps) => {
  const location = useLocation();

  useEffect(() => {
    // Update document title
    document.title = seoProps?.title || defaultSEO.title;

    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', seoProps?.description || defaultSEO.description);
    }

    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', seoProps?.keywords || defaultSEO.keywords);
    }

    // Update Open Graph title
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', seoProps?.ogTitle || seoProps?.title || defaultSEO.ogTitle);
    }

    // Update Open Graph description
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', seoProps?.ogDescription || seoProps?.description || defaultSEO.ogDescription);
    }

    // Update Twitter title
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', seoProps?.ogTitle || seoProps?.title || defaultSEO.ogTitle);
    }

    // Update Twitter description
    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', seoProps?.ogDescription || seoProps?.description || defaultSEO.ogDescription);
    }

    // Update Open Graph + Twitter image
    if (seoProps?.ogImage) {
      const absolute = seoProps.ogImage.startsWith('http')
        ? seoProps.ogImage
        : `${window.location.origin}${seoProps.ogImage.startsWith('/') ? '' : '/'}${seoProps.ogImage}`;
      ['meta[property="og:image"]', 'meta[property="og:image:secure_url"]', 'meta[name="twitter:image"]']
        .forEach(sel => {
          const el = document.querySelector(sel);
          if (el) el.setAttribute('content', absolute);
        });
    }

    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical && seoProps?.canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    if (canonical && seoProps?.canonical) {
      canonical.setAttribute('href', seoProps.canonical);
    }

    // Update hreflang alternates (remove previous, then inject current set)
    document.querySelectorAll('link[rel="alternate"][data-seo-hreflang]').forEach(el => el.remove());
    if (seoProps?.hreflangs?.length) {
      seoProps.hreflangs.forEach(({ hreflang, href }) => {
        const link = document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', hreflang);
        link.setAttribute('href', href);
        link.setAttribute('data-seo-hreflang', 'true');
        document.head.appendChild(link);
      });
    }

    // Track page view in Google Analytics
    if (window.gtag) {
      window.gtag('config', 'G-JET36TV0DK', {
        page_path: location.pathname,
        page_title: seoProps?.title || defaultSEO.title,
      });
    }
  }, [seoProps, location]);
};

// Declare gtag as a global function
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}